import React from 'react';
import { IconButton, Button, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Textarea, useToast, Tooltip, Box, Menu, MenuButton, MenuList, MenuItem, SimpleGrid, Text, Image as ChakraImage } from '@chakra-ui/react';
import { Share2, Instagram, Twitter, Facebook, Copy } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { createShortLink, createPublicShortLink } from '../../services/shortlinks';
import { Article, getArticleMatchLink } from '../../services/articles';
import { API_URL } from '../../services/api';
import { composeInstagramPostFromArticle, composeInstagramPostFromActivity, MatchSnapshot, stripHtml, formatDateTime, cleanVenue } from '../../services/instagram';
import { generateInstagramAI, generateInstagramImagesAI } from '../../services/ai';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import AILoadingModal from '../common/AILoadingModal';

interface Props {
  article?: Article;
  activity?: any;
  match?: MatchSnapshot | null;
  targetUrl?: string;
  size?: 'sm' | 'md' | 'lg';
  placement?: 'fixed' | 'inline';
  mr?: number | string;
  mb?: number | string;
  zIndex?: number;
  variant?: 'button' | 'icon';
  onGenerated?: (text: string, shortUrl: string) => void;
  align?: 'left' | 'right';
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const InstagramGeneratorButton: React.FC<Props> = ({
  article,
  activity,
  match,
  targetUrl,
  size = 'md',
  placement = 'fixed',
  mr = 6,
  mb = 6,
  zIndex = 40,
  variant = 'icon',
  onGenerated,
  align = 'right',
  position = 'top-right',
}) => {
  const { user } = useAuth();
  const role = String(user?.role || '').toLowerCase();
  const isAdmin = role === 'admin';
  const { data: publicSettings } = usePublicSettings();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [text, setText] = React.useState('');
  const [shortUrl, setShortUrl] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [aiProgress, setAiProgress] = React.useState<number | undefined>(undefined);
  const [aiStartTime, setAiStartTime] = React.useState<number | null>(null);
  const [images, setImages] = React.useState<string[]>([]);

  // Build deterministic campaign id for UTM and shortlink code
  const campaignId = React.useMemo(() => {
    if (article?.id) return `article-${article.id}`;
    if (activity?.id) return `activity-${activity.id}`;
    return 'share';
  }, [article?.id, activity?.id]);

  const computeTarget = () => {
    if (targetUrl) return targetUrl;
    if (typeof window !== 'undefined') return window.location.href;
    return '';
  };

  const withUtm = (urlStr: string) => {
    try {
      const u = new URL(urlStr, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      if (!u.searchParams.get('utm_source')) u.searchParams.set('utm_source', 'instagram');
      if (!u.searchParams.get('utm_medium')) u.searchParams.set('utm_medium', 'social');
      if (!u.searchParams.get('utm_campaign')) u.searchParams.set('utm_campaign', campaignId);
      return u.toString();
    } catch {
      return urlStr;
    }
  };

  const handleGenerate = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      setLoading(true);
      setAiProgress(0);
      setAiStartTime(Date.now());
      setImages([]);
      
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setAiProgress(prev => {
          if (prev === undefined || prev >= 95) return prev;
          return Math.min(prev + Math.random() * 20 + 10, 95);
        });
      }, 1500);

      const fullUrl = withUtm(computeTarget());
      if (!fullUrl) throw new Error('Nelze zjistit URL článku/aktivity');

      // Deterministic shortlink code to keep link stable across generations
      const platformCode = article?.id ? `ig-a${article.id}` : (activity?.id ? `ig-e${activity.id}` : 'ig-share');
      const payload = {
        target_url: fullUrl,
        title: article?.title || activity?.title || 'Link',
        source_type: article ? 'article' : (activity ? 'event' : 'other'),
        source_id: article?.id || activity?.id,
        code: platformCode,
      } as any;
      let sUrl = '';
      try {
        const res = await createShortLink(payload);
        sUrl = res?.short_url || '';
      } catch (err) {
        // Fallback to public shortlink (deterministic per URL) or use long URL
        try {
          const resPub = await createPublicShortLink({ target_url: fullUrl, title: article?.title || activity?.title || 'Link' });
          sUrl = resPub?.short_url || fullUrl;
        } catch {
          sUrl = fullUrl;
        }
      }
      setShortUrl(sUrl || fullUrl);

      const clubName = publicSettings?.club_name || undefined;
      let composed = '';
      if (article) {
        let resolvedMatch = match || null;
        if (!resolvedMatch && article?.id) {
          try {
            const link = await getArticleMatchLink(article.id);
            const extId = (link as any)?.external_match_id;
            if (extId) {
              try {
                const origin = new URL(API_URL, window.location.origin).origin;
                const resp = await fetch(`${origin}/cache/prefetch/facr_club_info.json`, { cache: 'no-cache' });
                if (resp.ok) {
                  const json = await resp.json();
                  const comps = Array.isArray(json?.competitions) ? json.competitions : [];
                  let snapshot: MatchSnapshot | null = null;
                  for (const c of comps) {
                    const matches = Array.isArray(c.matches) ? c.matches : [];
                    for (const m of matches) {
                      const mid = String(m.match_id || m.id);
                      if (mid === String(extId)) {
                        let score = '';
                        if (m?.score && m.score !== 'vs') score = String(m.score);
                        else if (m?.result_home != null && m?.result_away != null) score = `${m.result_home}:${m.result_away}`;
                        snapshot = {
                          external_match_id: String(extId),
                          competition: String(c.name || ''),
                          date_time: String(m.date_time || m.date || ''),
                          venue: m.venue ? String(m.venue) : undefined,
                          home: String(m.home || m.home_team || ''),
                          away: String(m.away || m.away_team || ''),
                          score,
                        };
                        break;
                      }
                    }
                    if (snapshot) break;
                  }
                  if (snapshot) resolvedMatch = snapshot;
                }
              } catch {}
            }
          } catch {}
        }
        // Try real AI caption generation first; fallback to template composer
        try {
          const ai = await generateInstagramAI({
            type: 'article',
            title: article.title,
            content: stripHtml(article.content),
            club_name: clubName,
            link: sUrl || fullUrl,
            category: (article as any)?.category?.name || (article as any)?.category_name,
            match: resolvedMatch ? {
              home: resolvedMatch.home,
              away: resolvedMatch.away,
              competition: resolvedMatch.competition,
              date_time: resolvedMatch.date_time ? formatDateTime(resolvedMatch.date_time) : undefined,
              venue: resolvedMatch.venue ? cleanVenue(resolvedMatch.venue) : undefined,
              score: resolvedMatch.score,
            } : undefined,
          });
          composed = ai?.text?.trim() || '';
        } catch {}
        if (!composed) {
          composed = composeInstagramPostFromArticle({ article, trackingUrl: sUrl || fullUrl, clubName, match: resolvedMatch });
        }
      } else if (activity) {
        // Try AI generation first
        try {
          const ai = await generateInstagramAI({
            type: 'event',
            title: String(activity?.title || ''),
            content: stripHtml(String(activity?.description || '')),
            club_name: clubName,
            link: sUrl || fullUrl,
          });
          composed = ai?.text?.trim() || '';
        } catch {}
        if (!composed) {
          composed = composeInstagramPostFromActivity({ activity, trackingUrl: sUrl || fullUrl, clubName });
        }
      } else {
        composed = `${clubName || 'Náš klub'}\n\n🔗 ${sUrl || fullUrl}`;
      }

      try {
        const primaryColor = (publicSettings as any)?.primary_color || '';
        const secondaryColor = (publicSettings as any)?.secondary_color || '';
        const imagePromptBase = article
          ? `Návrh instagramového obrázku k článku "${article.title}" pro oficiální profil fotbalového klubu${clubName ? ' ' + clubName : ''}.`
          : activity
          ? `Návrh instagramového obrázku k aktivitě "${activity.title}" pro oficiální profil fotbalového klubu${clubName ? ' ' + clubName : ''}.`
          : `Návrh univerzálního instagramového obrázku pro oficiální profil fotbalového klubu${clubName ? ' ' + clubName : ''}.`;

        let colorHint = '';
        if (primaryColor || secondaryColor) {
          colorHint = ` Klubové barvy: ${primaryColor || ''}${primaryColor && secondaryColor ? ', ' : ''}${secondaryColor || ''}.`;
        }

        const imagePrompt = `${imagePromptBase} Zobraz stadion, hráče nebo fanoušky našeho klubu, žádné loga soupeře ani text v obrázku.${colorHint} Styl: realistický, moderní, sportovní, poměr stran 4:5, bez textu, vhodné jako hlavní vizuál příspěvku.`;
        const imgResp = await generateInstagramImagesAI({
          prompt: imagePrompt,
          aspect: '4:5',
          count: 2,
        });
        if (Array.isArray(imgResp?.urls) && imgResp.urls.length > 0) {
          setImages(imgResp.urls);
        }
      } catch (imgErr) {
        console.error('Instagram image generation failed', imgErr);
      }
      
      clearInterval(progressInterval);
      setAiProgress(100);
      setText(composed);
      onGenerated?.(composed, sUrl || fullUrl);
      
      setTimeout(() => {
        onOpen();
      }, 500); // Brief moment to show 100% completion
    } catch (err: any) {
      toast({ status: 'error', title: 'Nelze vygenerovat příspěvek', description: err?.message || 'Zkuste to prosím znovu.' });
    } finally {
      setTimeout(() => {
        setLoading(false);
        setAiProgress(undefined);
        setAiStartTime(null);
      }, 1000); // Brief moment to show 100% completion or clear on error
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ status: 'success', title: 'Zkopírováno', description: 'Text příspěvku byl zkopírován do schránky.' });
    } catch {
      toast({ status: 'warning', title: 'Nelze kopírovat', description: 'Zkopírujte prosím ručně.' });
    }
  };

  // Build share URL with platform-specific UTMs (base long URL)
  const buildShareUrl = (platform: 'instagram' | 'twitter' | 'facebook' | 'copy'): string => {
    const base = computeTarget();
    try {
      const u = new URL(base, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      if (!u.searchParams.get('utm_source')) u.searchParams.set('utm_source', platform);
      if (!u.searchParams.get('utm_medium')) u.searchParams.set('utm_medium', 'social');
      if (!u.searchParams.get('utm_campaign')) u.searchParams.set('utm_campaign', campaignId);
      return u.toString();
    } catch {
      return base;
    }
  };

  // Create or reuse a public shortlink for visitors for a given long URL
  const getPublicShortUrl = async (longUrl: string): Promise<string> => {
    try {
      const res = await createPublicShortLink({ target_url: longUrl, title: article?.title || activity?.title });
      return res?.short_url || longUrl;
    } catch {
      return longUrl;
    }
  };

  const handleShareClick = async (platform: 'instagram' | 'twitter' | 'facebook' | 'copy') => {
    const longUrl = buildShareUrl(platform);
    const sUrl = await getPublicShortUrl(longUrl);
    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(sUrl);
        toast({ status: 'success', title: 'Krátký odkaz zkopírován' });
      } catch {
        toast({ status: 'warning', title: 'Kopírování se nezdařilo' });
      }
      return;
    }
    if (platform === 'twitter') {
      const text = article?.title || activity?.title || '';
      const tw = `https://twitter.com/intent/tweet?url=${encodeURIComponent(sUrl)}&text=${encodeURIComponent(text)}`;
      window.open(tw, '_blank');
      return;
    }
    if (platform === 'facebook') {
      const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(sUrl)}`;
      window.open(fb, '_blank');
      return;
    }
    // Instagram has no official web share with prefilled text; open site and copy short link
    try {
      await navigator.clipboard.writeText(sUrl);
      toast({ status: 'info', title: 'Krátký odkaz zkopírován', description: 'Vložte jej do Instagramu.' });
    } catch {}
    window.open('https://www.instagram.com/', '_blank');
  };

  const AdminButtonEl = (
    <Tooltip label="Vygenerovat Instagram příspěvek" placement="right">
      {variant === 'icon' ? (
        <IconButton aria-label="Instagram příspěvek" icon={<Share2 size={18} />} colorScheme="brand" onClick={handleGenerate} isDisabled={loading} size={size} />
      ) : (
        <Button leftIcon={<Share2 size={18} />} colorScheme="brand" onClick={handleGenerate} isDisabled={loading} size={size}>
          Instagram příspěvek
        </Button>
      )}
    </Tooltip>
  );

  const VisitorShareEl = (
    <Menu placement="top-start">
      <MenuButton
        as={IconButton}
        aria-label="Sdílet"
        icon={<Share2 size={18} />}
        variant="solid"
        colorScheme="brand"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
      />
      <MenuList>
        <MenuItem onClick={() => handleShareClick('instagram')} icon={<Instagram size={16} />}>Instagram</MenuItem>
        <MenuItem onClick={() => handleShareClick('twitter')} icon={<Twitter size={16} />}>Twitter</MenuItem>
        <MenuItem onClick={() => handleShareClick('facebook')} icon={<Facebook size={16} />}>Facebook</MenuItem>
        <MenuItem onClick={() => handleShareClick('copy')} icon={<Copy size={16} />}>Kopírovat odkaz</MenuItem>
      </MenuList>
    </Menu>
  );

  return (
    <>
      {placement === 'fixed' ? (
        <Box position="fixed" bottom={mb} zIndex={zIndex} {...(align === 'left' ? { left: mr } : { right: mr })}>
          {isAdmin ? AdminButtonEl : VisitorShareEl}
        </Box>
      ) : (
        <Box
          position="absolute"
          zIndex={zIndex}
          {...(position === 'top-left' ? { top: 2, left: 2 } : {})}
          {...(position === 'top-right' ? { top: 2, right: 2 } : {})}
          {...(position === 'bottom-left' ? { bottom: 2, left: 2 } : {})}
          {...(position === 'bottom-right' ? { bottom: 2, right: 2 } : {})}
        >
          {isAdmin ? AdminButtonEl : VisitorShareEl}
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Instagram příspěvek</ModalHeader>
          <ModalBody>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} fontFamily="mono" />
            {images.length > 0 && (
              <Box mt={4}>
                <Text fontSize="sm" mb={2}>Návrhy obrázků pro Instagram (Grok):</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  {images.map((url, idx) => (
                    <Box key={idx}>
                      <ChakraImage src={url} alt={`Instagram obrázek ${idx + 1}`} borderRadius="md" w="100%" objectFit="cover" />
                      <Button
                        mt={2}
                        size="xs"
                        variant="outline"
                        onClick={() => { window.open(url, '_blank'); }}
                      >
                        Otevřít v novém okně
                      </Button>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            )}
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="outline" onClick={handleCopy}>Kopírovat</Button>
            <Button onClick={onClose}>Zavřít</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AILoadingModal
        isOpen={loading}
        onClose={() => {}} // Can't close while AI is working
        title="AI generuje Instagram příspěvek"
        message="Pracuji na vytvoření příspěvku pro Instagram..."
        progress={aiProgress}
        estimatedTime={aiStartTime ? 20 : undefined} // 20 seconds estimated
      />
    </>
  );
};

export default InstagramGeneratorButton;
